import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import apiClient from '../services/api';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const Reports = () => {
  const [selectedTable, setSelectedTable] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  });
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, runExport] = useActionLock();

  useEffect(() => {
    const fetchTables = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/admin/export/tables/');

        let tablesData = [];
        if (Array.isArray(response.data)) {
          tablesData = response.data;
        } else if (response.data?.tables && Array.isArray(response.data.tables)) {
          tablesData = response.data.tables;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          tablesData = response.data.data;
        } else if (response.data && typeof response.data === 'object') {
          tablesData = Object.entries(response.data).map(([key, value]) => ({
            key,
            name: typeof value === 'string' ? value : value?.name || key,
            ...((typeof value === 'object') ? value : {})
          }));
        }

        setTables(tablesData);

        if (tablesData.length > 0) {
          setSelectedTable(tablesData[0].key || tablesData[0].id || Object.keys(tablesData[0])[0]);
        }
      } catch (error) {
        console.error('Error fetching tables:', error);
        setTables([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTables();
  }, []);



  // Get selected table info
  const getSelectedTableInfo = () => {
    const table = tables.find(t => (t.key || t.id) === selectedTable);
    return table || null;
  };

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleExport = async (format = 'excel') => {
    if (!selectedTable) {
      alert('Please select a table');
      return;
    }

    try {
      const response = await apiClient.post(
        `/admin/export/${selectedTable}/${format}/`,
        {
          start_date: fromDate,
          end_date: toDate,
        },
        {
          responseType: 'blob',
        }
      );

      const contentType = response.headers['content-type'] || '';
      const isActualExcel = contentType.includes('spreadsheet') ||
        contentType.includes('excel') ||
        contentType.includes('application/vnd.openxmlformats-officedocument') ||
        contentType.includes('application/vnd.ms-excel');

      const actualExtension = (format === 'csv' || !isActualExcel) ? 'csv' : 'xlsx';
      const mimeType = actualExtension === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      const safeFromDate = fromDate.replace(/-/g, '_');
      const safeToDate = toDate.replace(/-/g, '_');
      let filename = `${selectedTable}_${safeFromDate}_to_${safeToDate}.${actualExtension}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      let errorMessage = 'Failed to export. Please try again.';

      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json.message || json.error) {
            errorMessage = json.message || json.error;
          }
        } catch (e) {
          // If parsing fails, use the generic error message
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    }
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#E2E8F0',
      },
      '&:hover fieldset': {
        borderColor: '#4E83BD',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4E83BD',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '14px',
      color: '#1F2937',
    },
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: 'white' }}
          >
            Export Report
          </Typography>
        </Box>
      </Paper>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            padding: { xs: 2, sm: 2.5 },
            backgroundColor: 'white',
            alignItems: { xs: 'stretch', md: 'flex-end' },
          }}
        >
          {/* Select Table */}
          <Box sx={{ flex: 1, minWidth: { md: '250px' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
              <TableChartOutlinedIcon sx={{ color: '#4E83BD', fontSize: 18 }} />
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                Select Table
              </Typography>
            </Box>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              disabled={loading}
              SelectProps={{
                IconComponent: KeyboardArrowDownIcon,
              }}
              sx={inputStyles}
            >
              {loading ? (
                <MenuItem value="">
                  <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
                </MenuItem>
              ) : tables.length === 0 ? (
                <MenuItem value="" disabled>
                  No tables available
                </MenuItem>
              ) : (
                tables.map((table, index) => (
                  <MenuItem key={table.key || table.id || index} value={table.key || table.id || index}>
                    {table.table_name || table.display_name || table.title || table.key || `Table ${index + 1}`}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Box>

          {/* From Date */}
          <Box sx={{ flex: 1, minWidth: { md: '180px' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
              <CalendarTodayOutlinedIcon sx={{ color: '#4E83BD', fontSize: 16 }} />
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                From Date
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              sx={inputStyles}
            />
          </Box>

          {/* To Date */}
          <Box sx={{ flex: 1, minWidth: { md: '180px' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
              <CalendarTodayOutlinedIcon sx={{ color: '#4E83BD', fontSize: 16 }} />
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                To Date
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              inputProps={{ min: fromDate }}
              sx={inputStyles}
            />
          </Box>
        </Box>
      </Paper>

      {/* Table Description Section */}
      {selectedTable && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid #E2E8F0',
            overflow: 'hidden',
          }}
        >

          <Box sx={{ padding: { xs: 2, sm: 2.5 }, backgroundColor: 'white' }}>
            {/* Table Info Header */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              {/* Left Section - Table Info */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    backgroundColor: '#EBF5FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <TableChartOutlinedIcon sx={{ color: '#4E83BD', fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#1F2937',
                      marginBottom: 0.5,
                    }}
                  >
                    {getSelectedTableInfo()?.table_name || getSelectedTableInfo()?.display_name || getSelectedTableInfo()?.title || selectedTable}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: '#64748B',
                    }}
                  >
                    {getSelectedTableInfo()?.description || 'Export data from this table within the selected date range.'}
                  </Typography>
                </Box>
              </Box>

              {/* Center Section - Selected Range */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  padding: '10px 16px',
                  backgroundColor: '#F8FAFC',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  flexShrink: 0,
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <CalendarTodayOutlinedIcon sx={{ color: '#F59E0B', fontSize: 20, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '11px', color: '#64748B', fontWeight: 500, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    Selected Range
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap' }}>
                    {formatDateForDisplay(fromDate)} to {formatDateForDisplay(toDate)}
                  </Typography>
                </Box>
              </Box>

              {/* Right Section - Export Buttons */}
              <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0, flex: { xs: 'unset', md: 1 }, justifyContent: { xs: 'stretch', md: 'flex-end' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                <ActionButton
                  variant="outlined"
                  fullWidth={false}
                  loading={exporting}
                  onClick={() => runExport(() => handleExport('excel'))}
                  loadingText="Exporting..."
                  startIcon={<GridOnOutlinedIcon sx={{ fontSize: 18 }} />}
                  disabled={!selectedTable}
                  sx={{
                    textTransform: 'none',
                    color: '#4E83BD',
                    fontSize: '13px',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontWeight: 600,
                    borderColor: '#4E83BD',
                    flex: { xs: 1, sm: 'unset' },
                    '&:hover': {
                      backgroundColor: '#EBF5FF',
                      borderColor: '#3A6A99',
                    },
                  }}
                >
                  Export Excel
                </ActionButton>
            
              </Box>
            </Box>


          </Box>

        </Paper>
      )}
    </Box>
  );
};

export default Reports;
